import React from 'react';

// Client-side mermaid renderer for the `diagram` block (DR-2026-271).
// Vendored from volto-plate (SPEC-PLATE-003 §8.5.1 hardening) with
// `diagram-`-scoped class names; volto-plate is untouched on its
// somersault track.
//
// SSR / first paint / error → render the SOURCE in a <pre> (graceful:
// searchable, no layout surprise, identical on server + client first
// render so hydration matches). Only AFTER mount does useEffect
// dynamically import mermaid and swap in the SVG — so:
//   • mermaid (~23 MB unpacked) stays in a LAZY chunk loaded only on
//     pages that actually contain a diagram;
//   • hydration is safe (server HTML === client first render === fallback).
// securityLevel:'strict' — mermaid sanitizes the SVG, no script/click.
export function MermaidDiagram({ source }) {
  const [svg, setSvg] = React.useState(null);
  const [errored, setErrored] = React.useState(false);
  // mermaid's render id must be a valid DOM id — useId() contains ':'.
  const rawId = React.useId();
  const renderId = `diagram-mermaid-${rawId.replace(/[^a-zA-Z0-9]/g, '')}`;

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const mermaid = (await import('mermaid')).default;
        // suppressErrorRendering:true is LOAD-BEARING — without it a syntax
        // error draws mermaid's "Syntax error in text" bomb graphic into the
        // DOM and orphans it (mermaid core ~1289/1308). With it, mermaid
        // cleans up and re-throws, so our catch shows the inline source.
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: 'strict',
          suppressErrorRendering: true,
        });
        const { svg: out } = await mermaid.render(renderId, source);
        if (!cancelled) {
          setSvg(out);
          setErrored(false);
        }
      } catch {
        if (!cancelled) {
          setSvg(null);
          setErrored(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [source, renderId]);

  if (svg) {
    // mermaid strict-mode output is sanitized SVG markup.
    return <div className="diagram-mermaid" dangerouslySetInnerHTML={{ __html: svg }} />;
  }
  return (
    <div
      className={`diagram-mermaid-fallback${errored ? ' diagram-mermaid-fallback--error' : ''}`}
    >
      {errored ? (
        <div className="diagram-mermaid-error-note">
          Diagram has a syntax error — showing source:
        </div>
      ) : null}
      <pre className="diagram-mermaid-source">{source}</pre>
    </div>
  );
}

export default MermaidDiagram;
